import { useEffect, useState, useCallback, useMemo } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import HottestCustomersWidget from "../components/HottestCustomersWidget";
import { ChartBar, WhatsappLogo, Funnel, Users, Buildings, FileText, Receipt, ShoppingCart, Truck, Wrench, Clock, IdentificationCard, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "Last 7 days" },
  { key: "month", label: "This month" },
  { key: "all", label: "All time" },
];
const DEPARTMENTS = [
  { key: "", label: "All departments" },
  { key: "marketing", label: "Marketing" },
  { key: "finance", label: "Finance" },
  { key: "purchase", label: "Purchase" },
  { key: "store_delivery", label: "Store & Delivery" },
  { key: "service_installation", label: "Service & Installation" },
  { key: "amc", label: "AMC" },
  { key: "hr", label: "HR" },
];
const MODULES = ["", "lead", "quotation", "invoice", "collection", "purchase", "vendor", "delivery", "service", "amc", "hr"];
const DOC_TYPES = ["", "quotation", "invoice", "receipt", "purchase-order", "delivery-challan", "installation-completion", "customer-statement"];

const KpiCard = ({ icon: Icon, label, value, accent = "slate", testId }) => {
  const colors = {
    slate: "text-slate-700 bg-slate-50 border-slate-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    blue: "text-blue-700 bg-blue-50 border-blue-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
  }[accent];
  return (
    <div data-testid={testId} className={`border ${colors} p-4`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} weight="bold" />
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">{label}</div>
      </div>
      <div className="text-3xl tracking-tighter font-bold tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>{value ?? 0}</div>
    </div>
  );
};

const WhatsAppDashboard = ({ user, logout }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [range, setRange] = useState("week");
  const [department, setDepartment] = useState("");
  const [module, setModule] = useState("");
  const [senderUserId, setSenderUserId] = useState("");
  const [docType, setDocType] = useState("");
  const [customer, setCustomer] = useState("");

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_user_id: user?.user_id || "",
        range,
        ...(department ? { department } : {}),
        ...(module ? { module } : {}),
        ...(senderUserId ? { sender_user_id: senderUserId } : {}),
        ...(docType ? { related_doc_type: docType } : {}),
        ...(customer ? { customer } : {}),
      });
      const r = await axios.get(`${API}/whatsapp/dashboard/stats?${params.toString()}`);
      setStats(r.data || null);
    } catch (e) {
      toast.error("Failed to load WhatsApp stats");
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.user_id, range, department, module, senderUserId, docType, customer]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    api.get(`/users`).then(r => setUsers((r.data || []).filter(u => u.role !== "customer"))).catch(() => {});
  }, []);

  const totals = stats?.totals || {};
  const byDept = stats?.by_department || [];
  const byModule = stats?.by_module || [];
  const byStaff = stats?.by_staff || [];
  const byTemplate = stats?.by_template || [];

  const topCount = useMemo(() => Math.max(1, ...byDept.map(d => d.count)), [byDept]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Phase 4b-3 · Insights</div>
          <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter font-bold flex items-center gap-3" style={{fontFamily: 'Outfit, sans-serif'}}>
            <WhatsappLogo size={36} weight="fill" className="text-emerald-600" />
            WhatsApp Dashboard
          </h1><HelpButton moduleKey="whatsapp_logs" /></div>
          <p className="text-base text-slate-700 mt-1">Department-wise click-to-send activity · {user?.role === "bde" ? "your data only" : user?.role === "manager" || user?.role === "admin" ? "all departments" : "your team scope"}</p>
        </div>

        {/* Filters */}
        <div data-testid="wa-dash-filters" className="bg-white border border-slate-200 p-4 mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block flex items-center gap-1"><Funnel size={10} /> Range</label>
            <select data-testid="wa-dash-range" value={range} onChange={e => setRange(e.target.value)} className="w-full rounded-none border border-slate-300 p-2 text-xs outline-none">
              {RANGES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Department</label>
            <select data-testid="wa-dash-department" value={department} onChange={e => setDepartment(e.target.value)} className="w-full rounded-none border border-slate-300 p-2 text-xs outline-none">
              {DEPARTMENTS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Module</label>
            <select data-testid="wa-dash-module" value={module} onChange={e => setModule(e.target.value)} className="w-full rounded-none border border-slate-300 p-2 text-xs outline-none">
              {MODULES.map(m => <option key={m} value={m}>{m || "All"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Staff</label>
            <select data-testid="wa-dash-staff" value={senderUserId} onChange={e => setSenderUserId(e.target.value)} className="w-full rounded-none border border-slate-300 p-2 text-xs outline-none">
              <option value="">All staff</option>
              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Doc Type</label>
            <select data-testid="wa-dash-doctype" value={docType} onChange={e => setDocType(e.target.value)} className="w-full rounded-none border border-slate-300 p-2 text-xs outline-none">
              {DOC_TYPES.map(d => <option key={d} value={d}>{d || "All"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Customer / Vendor</label>
            <input data-testid="wa-dash-customer" value={customer} onChange={e => setCustomer(e.target.value)} placeholder="search…" className="w-full rounded-none border border-slate-300 p-2 text-xs outline-none" />
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <KpiCard testId="kpi-today" icon={Clock} label="Today" value={totals.today} accent="emerald" />
          <KpiCard testId="kpi-week" icon={Clock} label="Last 7 days" value={totals.week} accent="emerald" />
          <KpiCard testId="kpi-month" icon={Clock} label="This month" value={totals.month} accent="emerald" />
          <KpiCard testId="kpi-manual" icon={CheckCircle} label="Manual Opened" value={totals.manual_opened} accent="blue" />
          <KpiCard testId="kpi-failed" icon={Warning} label="Failed / no #" value={totals.failed_or_missing_number} accent="rose" />
          <KpiCard testId="kpi-total" icon={ChartBar} label="All-time" value={totals.all_time} accent="slate" />
        </div>

        {/* Module-specific KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <KpiCard testId="kpi-quote" icon={FileText} label="Quotation" value={totals.quotation_clicks} accent="blue" />
          <KpiCard testId="kpi-invoice" icon={Receipt} label="Invoice" value={totals.invoice_clicks} accent="amber" />
          <KpiCard testId="kpi-payreminder" icon={Warning} label="Payment Reminder" value={totals.payment_reminder_clicks} accent="rose" />
          <KpiCard testId="kpi-vendor" icon={ShoppingCart} label="Vendor / PO" value={totals.vendor_clicks} accent="slate" />
          <KpiCard testId="kpi-service" icon={Wrench} label="Service" value={totals.service_clicks} accent="emerald" />
          <KpiCard testId="kpi-hr" icon={IdentificationCard} label="HR" value={totals.hr_clicks} accent="slate" />
        </div>

        {/* Hottest customers + dept bars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 bg-white border border-slate-200">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-2">
              <Buildings size={14} weight="bold" className="text-[#002FA7]" />
              <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">Department-wise Activity</h3>
            </div>
            <div data-testid="wa-dash-by-dept" className="p-5 space-y-3">
              {loading ? <div className="text-xs text-slate-500">Loading…</div> :
              byDept.length === 0 ? <div className="text-xs text-slate-400 text-center py-8">No activity in this range.</div> :
              byDept.map(d => (
                <div key={d.key} data-testid={`bar-dept-${d.key}`} className="space-y-1">
                  <div className="flex justify-between text-xs"><span className="font-semibold">{d.label}</span><span className="tabular-nums text-slate-500">{d.count}</span></div>
                  <div className="h-2 bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(d.count / topCount) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <HottestCustomersWidget user={user} />
        </div>

        {/* By staff + module + template tables */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-2">
              <Users size={14} weight="bold" className="text-[#002FA7]" />
              <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">Staff-wise</h3>
            </div>
            <div data-testid="wa-dash-by-staff" className="max-h-72 overflow-y-auto">
              <table className="w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-200"><th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">Staff</th><th className="text-right p-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">Clicks</th></tr></thead>
                <tbody>
                  {byStaff.length === 0 ? <tr><td colSpan={2} className="p-6 text-center text-slate-400 text-xs">No data</td></tr> :
                  byStaff.map(s => (
                    <tr key={s.user_id} data-testid={`staff-row-${s.user_id}`} className="border-b border-slate-100">
                      <td className="p-2 text-xs"><div className="font-semibold">{s.name}</div><div className="text-slate-500">{s.role}{s.department ? ` · ${s.department}` : ""}</div></td>
                      <td className="p-2 text-right tabular-nums font-semibold text-emerald-700">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-2">
              <Truck size={14} weight="bold" className="text-[#002FA7]" />
              <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">Module-wise</h3>
            </div>
            <div data-testid="wa-dash-by-module" className="max-h-72 overflow-y-auto">
              <table className="w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-200"><th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">Module</th><th className="text-right p-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">Clicks</th></tr></thead>
                <tbody>
                  {byModule.length === 0 ? <tr><td colSpan={2} className="p-6 text-center text-slate-400 text-xs">No data</td></tr> :
                  byModule.map(m => (
                    <tr key={m.module} className="border-b border-slate-100">
                      <td className="p-2 text-xs font-semibold capitalize">{m.module}</td>
                      <td className="p-2 text-right tabular-nums font-semibold text-emerald-700">{m.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-2">
              <FileText size={14} weight="bold" className="text-[#002FA7]" />
              <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">Template usage</h3>
            </div>
            <div data-testid="wa-dash-by-template" className="max-h-72 overflow-y-auto">
              <table className="w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-200"><th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">Template</th><th className="text-right p-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">Used</th></tr></thead>
                <tbody>
                  {byTemplate.length === 0 ? <tr><td colSpan={2} className="p-6 text-center text-slate-400 text-xs">No data</td></tr> :
                  byTemplate.map(t => (
                    <tr key={t.template_id} className="border-b border-slate-100">
                      <td className="p-2 text-xs"><div className="font-semibold">{t.name}</div><div className="text-slate-500 capitalize">{t.module}</div></td>
                      <td className="p-2 text-right tabular-nums font-semibold text-emerald-700">{t.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppDashboard;
